import GenericQueue from '../../../GenericQueue'
import { CIMSTags } from 'components/CIMSTags'
import { QueuableRequestStatus, Report } from 'shared'
import { Space } from 'antd'
import { useState, useEffect } from 'react'
import { useGlobalState } from 'context/GlobalStateContext'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import {
  useQueuedBatchReportRequestsQuery,
  useCancelBatchReportRequestMutation,
  QueuedBatchReportRequestsQuery,
} from 'schema'
import {
  DefaultQueryHookOptions,
  DefaultMutationHookOptions,
} from 'utils/defaults'
import { dateFormatter } from '../../../../../../../../../utils/dateFormatter'

export type QueuedBatchReportRequest =
  QueuedBatchReportRequestsQuery['batchReportRequests']['records'][0]

const Queue: React.FC = () => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { Generating, Queued } = QueuableRequestStatus
  const [active, setActive] = useState<QueuedBatchReportRequest | undefined>()
  const [requests, setRequests] = useState<QueuedBatchReportRequest[]>([])
  const { data, refetch, loading } = useQueuedBatchReportRequestsQuery({
    ...DefaultQueryHookOptions,
    variables: {
      batchReportRequestListInput: {
        projectId: { equals: projectId },
        statusId: { list: [Queued.id, Generating.id] },
      },
    },
  })
  useEffect(() => {
    if (!data?.batchReportRequests) return
    const requests = data.batchReportRequests.records
    setActive(requests.find(({ statusId }) => statusId === Generating.id))
    setRequests(requests.filter(({ statusId }) => statusId === Queued.id))
  }, [Generating, Queued, data?.batchReportRequests])
  const [cancel] = useCancelBatchReportRequestMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () =>
      simpleSuccessNotification('Batch Report Request successfully canceled!'),
  })
  return (
    <GenericQueue
      active={active}
      queued={{
        requests,
        onCancel: ({ id }) => cancel({ variables: { id } }),
      }}
      refetch={refetch}
      fetching={loading}
      options={({
        json: {
          arguments: { reportIds, dateRange, payItemNoGeometries },
        },
      }) => [
        {
          label: 'Reports',
          children: (
            <CIMSTags
              tags={reportIds.map((id) => ({
                text: Report.fromIdOrFail(id).name,
              }))}
            />
          ),
        },
        {
          label: 'Pay Item(s)',
          hidden: !payItemNoGeometries.length,
          children: (
            <CIMSTags
              tags={payItemNoGeometries.map(({ label }) => ({
                text: label,
              }))}
            />
          ),
        },
        {
          label: 'Date Range',
          hidden: !dateRange,
          children: (
            <Space size="small" align="center">
              {dateFormatter.shortDatetime(dateRange?.from)}-
              {dateFormatter.shortDatetime(dateRange?.to)}
            </Space>
          ),
        },
      ]}
    />
  )
}

export default Queue
